import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import medaiLogo from '@/assets/medai-cite-logo-64.webp';
import { useLanguage } from '@/contexts/LanguageContext';
import { LanguageSelector } from '@/components/LanguageSelector';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { 
  BookOpen, 
  Database, 
  Download, 
  Shield, 
  Clock, 
  CheckCircle2,
  Sparkles,
  ArrowRight,
  Search,
  FileCheck,
  Stethoscope,
  Menu,
  Crown,
  LogIn,
  GraduationCap,
  RotateCcw
} from 'lucide-react';
import { Helmet } from 'react-helmet';

const Landing = () => {
  const navigate = useNavigate();
  const { language, setLanguage, t } = useLanguage();
  const [weeklySearches, setWeeklySearches] = useState<number | ''>('');
  const [minutesPerSearch, setMinutesPerSearch] = useState<number | ''>('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  // Demo Mode Animation States
  const [demoTypedText, setDemoTypedText] = useState('');
  const [demoPhase, setDemoPhase] = useState<'idle' | 'typing' | 'loading' | 'results'>('idle');
  const [demoKey, setDemoKey] = useState(0);

  // Check if user is already logged in
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        navigate('/search');
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        navigate('/search');
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  // Demo Mode Animation Effect
  useEffect(() => {
    // Reset states when demo restarts
    setDemoTypedText('');
    setDemoPhase('idle');

    const demoText = t.landingDemo.exampleQuery;
    let typingTimeout: NodeJS.Timeout;
    let loadingTimeout: NodeJS.Timeout;
    let resultsTimeout: NodeJS.Timeout;

    // Start typing animation after component mount
    const startDemo = setTimeout(() => {
      setDemoPhase('typing');
      let currentIndex = 0;

      const typeChar = () => {
        if (currentIndex <= demoText.length) {
          setDemoTypedText(demoText.slice(0, currentIndex));
          currentIndex++;
          typingTimeout = setTimeout(typeChar, 80); // 80ms per character
        } else {
          // Typing finished, start loading
          loadingTimeout = setTimeout(() => {
            setDemoPhase('loading');
            
            // Show results after loading
            resultsTimeout = setTimeout(() => {
              setDemoPhase('results');
            }, 1000);
          }, 300);
        }
      };

      typeChar();
    }, 500); // Start after 500ms

    return () => {
      clearTimeout(startDemo);
      clearTimeout(typingTimeout);
      clearTimeout(loadingTimeout);
      clearTimeout(resultsTimeout);
    };
  }, [demoKey]);

  const restartDemo = () => {
    setDemoKey(prev => prev + 1);
  };

  const calculateTimeSaved = () => {
    // Check if values are valid numbers
    if (weeklySearches === '' || minutesPerSearch === '' || 
        weeklySearches <= 0 || minutesPerSearch <= 0) {
      return { hoursPerWeek: 0, minutesRemaining: 0, hoursPerMonth: 0, timeSaved: 0, hasEnoughData: false };
    }
    
    const currentTime = weeklySearches * minutesPerSearch;
    const newTime = weeklySearches * 2; // 2 minutes with MedAI Cite
    const timeSaved = currentTime - newTime;
    const hoursPerWeek = Math.floor(timeSaved / 60);
    const minutesRemaining = timeSaved % 60;
    const hoursPerMonth = Math.floor((timeSaved * 4) / 60);
    const hasEnoughData = timeSaved > 0;
    
    return { hoursPerWeek, minutesRemaining, hoursPerMonth, timeSaved, hasEnoughData };
  };

  const { hoursPerWeek, minutesRemaining, hoursPerMonth, timeSaved, hasEnoughData } = calculateTimeSaved();

  const benefits = [
    {
      icon: BookOpen,
      title: t.landingBenefits.benefit1Title,
      description: t.landingBenefits.benefit1Desc,
    },
    {
      icon: Database,
      title: t.landingBenefits.benefit2Title,
      description: t.landingBenefits.benefit2Desc,
    },
    {
      icon: Download,
      title: t.landingBenefits.benefit3Title,
      description: t.landingBenefits.benefit3Desc,
    },
    {
      icon: Shield,
      title: t.landingBenefits.benefit4Title,
      description: t.landingBenefits.benefit4Desc,
    },
  ];


  return (
    <>
      <Helmet>
        <title>MedAI Cite - IA mèdica per trobar articles mèdics i cites automàtiques</title>
        <meta name="description" content="Plataforma d'IA mèdica per generar cites automàtiques de articles mèdics. Cerca en bases de dades d'investigació mèdica com PubMed amb intel·ligència artificial." />
        <link rel="canonical" href="https://medai-cite.com/" />
        
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://medai-cite.com/" />
        <meta property="og:title" content="MedAI Cite - IA mèdica per trobar articles mèdics i cites automàtiques" />
        <meta property="og:description" content="Plataforma d'IA mèdica per generar cites automàtiques de articles mèdics. Cerca en bases de dades d'investigació mèdica com PubMed amb intel·ligència artificial." />
        <meta property="og:image" content="https://medai-cite.com/medai-cite-logo-og.png" />
        <meta property="og:site_name" content="MedAI Cite" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://medai-cite.com/" />
        <meta name="twitter:title" content="MedAI Cite - IA mèdica per trobar articles mèdics" />
        <meta name="twitter:description" content="Plataforma d'IA mèdica per generar cites automàtiques de articles mèdics. Cerca en investigació mèdica amb intel·ligència artificial." />
        <meta name="twitter:image" content="https://medai-cite.com/medai-cite-logo-og.png" />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-b from-primary/5 via-background to-background">
      {/* Navigation */}
      <nav className="border-b bg-background/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src={medaiLogo}
              alt="MedAI Cite Logo" 
              className="h-10 w-10 object-contain"
              width="40"
              height="40"
            />
            <span className="text-xl font-bold text-foreground">MedAI Cite</span>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-3">
            <Button 
              size="sm"
              className="bg-[#0057FF] hover:bg-[#3377FF] text-white font-bold rounded-lg px-4 transition-colors duration-300"
              onClick={() => navigate('/pricing')}
            >
              {t.landingNav.plans}
            </Button>
            <LanguageSelector 
              currentLanguage={language} 
              onLanguageChange={(lang) => setLanguage(lang as 'es' | 'en' | 'ca')}
            />
            <Button variant="outline" onClick={() => navigate('/auth')}>
              {t.landingNav.login}
            </Button>
          </div>

          {/* Mobile Navigation */}
          <div className="md:hidden">
            <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon">
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">Abrir menú</span>
                </Button>
              </SheetTrigger>
              <SheetContent className="w-[300px] sm:w-[400px]">
                <SheetHeader>
                  <SheetTitle>MedAI Cite</SheetTitle>
                </SheetHeader>
                
                <div className="flex flex-col gap-4 mt-6">
                  {/* Language Selector */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Idioma</span>
                    <LanguageSelector 
                      currentLanguage={language} 
                      onLanguageChange={(lang) => setLanguage(lang as 'es' | 'en' | 'ca')}
                    />
                  </div>

                  <Separator />

                  {/* Plans Button */}
                  <Button 
                    className="w-full justify-start bg-[#0057FF] hover:bg-[#3377FF] text-white font-bold transition-colors duration-300"
                    onClick={() => {
                      navigate('/pricing');
                      setIsMenuOpen(false);
                    }}
                  >
                    <Crown className="mr-2 h-4 w-4" />
                    {t.landingNav.plans}
                  </Button>
                  
                  {/* Login Button */}
                  <Button 
                    variant="default" 
                    className="w-full justify-start"
                    onClick={() => {
                      navigate('/auth');
                      setIsMenuOpen(false);
                    }}
                  >
                    <LogIn className="mr-2 h-4 w-4" />
                    {t.landingNav.login}
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative container mx-auto px-4 pt-20 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-[var(--gradient-hero)] -z-10" />
        
        <div className="max-w-4xl mx-auto text-center animate-fade-in">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6 backdrop-blur-sm border border-primary/20">
            <Sparkles className="h-4 w-4" />
            {t.landingHero.badge}
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 leading-tight">
            {t.landingHero.title}{' '}
            <span className="text-primary">{t.landingHero.titleHighlight}</span>{t.landingHero.titleEnd}
          </h1>
          
          <p className="text-lg md:text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
            {t.landingHero.subtitle}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              size="lg" 
              className="text-lg px-10 h-16 shadow-[var(--shadow-card)] hover:shadow-xl hover:scale-[1.08] hover:brightness-110 transition-all duration-300 ease-out"
              onClick={() => navigate('/auth')}
            >
              {t.landingHero.ctaPrimary}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="text-lg px-8 h-16 hover:bg-accent transition-colors"
              onClick={() => {
                document.getElementById('benefits')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              {t.landingHero.ctaSecondary}
            </Button>
          </div>
          
          {/* Trust indicator below hero CTA */}
          <p className="text-[15px] text-muted-foreground/85 mt-6 font-medium">
            {t.trustIndicators.heroSubtext}
          </p>
        </div>
      </section>

      {/* Demo Mode Section */}
      <section className="container mx-auto px-4 py-16 bg-gradient-to-br from-primary/5 to-accent/10">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12 animate-fade-in">
            <div className="inline-flex items-center gap-2 bg-accent/20 text-foreground px-4 py-2 rounded-full text-sm font-medium mb-4 border border-primary/20">
              <Sparkles className="h-4 w-4 text-primary" />
              {t.landingDemo.badge}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t.landingDemo.title}
            </h2>
            <p className="text-lg text-muted-foreground mb-6">
              {language === 'ca' 
                ? 'Així és com MedAI Cite troba i resumeix articles mèdics en segons.'
                : language === 'es'
                ? 'Así es como MedAI Cite encuentra y resume artículos médicos en segundos.'
                : 'This is how MedAI Cite finds and summarizes medical articles in seconds.'}
            </p>
          </div>

          {/* Demo Search Bar */}
          <div className="max-w-3xl mx-auto mb-8">
            <div className="relative">
              <div className="relative flex items-center bg-background border-2 border-primary/30 rounded-lg shadow-lg overflow-hidden">
                <Search className="absolute left-4 h-5 w-5 text-muted-foreground pointer-events-none" />
                <Input
                  value={demoTypedText}
                  readOnly
                  className="pl-12 pr-4 h-14 text-lg border-0 focus-visible:ring-0 cursor-default bg-background"
                  placeholder={language === 'ca' ? 'Escriu la teva cerca mèdica...' : language === 'es' ? 'Escribe tu búsqueda médica...' : 'Type your medical search...'}
                />
                {demoPhase === 'typing' && (
                  <span className="absolute right-4 w-0.5 h-6 bg-primary animate-pulse" />
                )}
              </div>
            </div>
          </div>

          {/* Loading Animation */}
          {demoPhase === 'loading' && (
            <div className="flex justify-center mb-8 animate-fade-in">
              <div className="flex items-center gap-3 bg-background px-6 py-3 rounded-full border border-primary/20 shadow-md">
                <div className="h-5 w-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <span className="text-sm font-medium text-foreground">
                  {language === 'ca' ? 'Cercant articles...' : language === 'es' ? 'Buscando artículos...' : 'Searching articles...'}
                </span>
              </div>
            </div>
          )}

          {/* Demo Results */}
          {demoPhase === 'results' && (
          <div className="grid gap-6 mb-8 animate-fade-in" style={{ animationDelay: '0.1s' }}>
            {/* Article 1 */}
            <Card className="hover:shadow-xl transition-all duration-300 rounded-xl border-2 shadow-md">
              <CardHeader>
                <div className="space-y-3">
                  <Badge variant="outline" className="bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20 font-semibold w-fit">
                    <BookOpen className="h-3.5 w-3.5 mr-1.5" />
                    PubMed
                  </Badge>
                  <CardTitle className="text-lg leading-tight">
                    Dislocation After Total Hip Arthroplasty: A Comprehensive Review
                  </CardTitle>
                  <CardDescription className="text-sm">
                    Smith J, Johnson K, Williams R • J Bone Joint Surg • 2023
                  </CardDescription>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-gradient-to-br from-primary/5 to-accent/10 p-4 rounded-lg border-l-4 border-primary shadow-sm">
                  <div className="flex items-start gap-2">
                    <Sparkles className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-foreground mb-1.5">
                        {language === 'ca' ? 'Context de l\'article' : language === 'es' ? 'Contexto del artículo' : 'Article context'}
                      </p>
                      <p className="text-sm text-foreground/90 leading-relaxed">
                        {language === 'ca' 
                          ? 'Aquest estudi analitza la freqüència de la luxació en les pròtesis de maluc, amb un seguiment de més de 5.000 pacients durant 10 anys. Els resultats mostren una incidència del 2-4% en pacients sense factors de risc.'
                          : language === 'es'
                          ? 'Este estudio analiza la frecuencia de la luxación en las prótesis de cadera, con un seguimiento de más de 5.000 pacientes durante 10 años. Los resultados muestran una incidencia del 2-4% en pacientes sin factores de riesgo.'
                          : 'This study analyzes the frequency of dislocation in hip prostheses, with a follow-up of over 5,000 patients for 10 years. Results show an incidence of 2-4% in patients without risk factors.'}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-accent/50 p-3 rounded-md">
                  <p className="text-sm text-muted-foreground font-medium mb-1">
                    {language === 'ca' ? 'Referència bibliogràfica (Vancouver):' : language === 'es' ? 'Referencia bibliográfica (Vancouver):' : 'Bibliographic reference (Vancouver):'}
                  </p>
                  <p className="text-sm">Smith J, Johnson K, Williams R. Dislocation After Total Hip Arthroplasty: A Comprehensive Review. J Bone Joint Surg. 2023;105(8):1234-1245.</p>
                </div>
              </CardContent>
            </Card>

            {/* Article 2 */}
            <Card className="hover:shadow-xl transition-all duration-300 rounded-xl border-2 shadow-md">
              <CardHeader>
                <div className="space-y-3">
                  <Badge variant="outline" className="bg-purple-500/10 text-purple-700 dark:text-purple-400 border-purple-500/20 font-semibold w-fit">
                    <GraduationCap className="h-3.5 w-3.5 mr-1.5" />
                    Semantic Scholar
                  </Badge>
                  <CardTitle className="text-lg leading-tight">
                    Risk Factors and Prevention Strategies for Hip Prosthesis Dislocation
                  </CardTitle>
                  <CardDescription className="text-sm">
                    Chen L, Rodriguez M, Anderson P • Orthop Rev • 2022
                  </CardDescription>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-gradient-to-br from-primary/5 to-accent/10 p-4 rounded-lg border-l-4 border-primary shadow-sm">
                  <div className="flex items-start gap-2">
                    <Sparkles className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-foreground mb-1.5">
                        {language === 'ca' ? 'Context de l\'article' : language === 'es' ? 'Contexto del artículo' : 'Article context'}
                      </p>
                      <p className="text-sm text-foreground/90 leading-relaxed">
                        {language === 'ca'
                          ? 'Revisió exhaustiva dels factors de risc que augmenten la probabilitat de luxació protèsica, incloent-hi l\'enfocament quirúrgic, la mida del cap femoral i les característiques del pacient. L\'estudi identifica estratègies preventives efectives.'
                          : language === 'es'
                          ? 'Revisión exhaustiva de los factores de riesgo que aumentan la probabilidad de luxación protésica, incluyendo el abordaje quirúrgico, el tamaño de la cabeza femoral y las características del paciente. El estudio identifica estrategias preventivas efectivas.'
                          : 'Comprehensive review of risk factors that increase the likelihood of prosthetic dislocation, including surgical approach, femoral head size, and patient characteristics. The study identifies effective prevention strategies.'}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-accent/50 p-3 rounded-md">
                  <p className="text-sm text-muted-foreground font-medium mb-1">
                    {language === 'ca' ? 'Referència bibliogràfica (Vancouver):' : language === 'es' ? 'Referencia bibliográfica (Vancouver):' : 'Bibliographic reference (Vancouver):'}
                  </p>
                  <p className="text-sm">Chen L, Rodriguez M, Anderson P. Risk Factors and Prevention Strategies for Hip Prosthesis Dislocation. Orthop Rev. 2022;14(3):456-467.</p>
                </div>
              </CardContent>
            </Card>
          </div>
          )}

          {/* Demo CTA */}
          {demoPhase === 'results' && (
          <div className="text-center animate-fade-in space-y-4" style={{ animationDelay: '0.2s' }}>
            <Button
              size="lg"
              className="text-lg px-10 h-16 shadow-[var(--shadow-card)] hover:shadow-xl hover:scale-[1.08] hover:brightness-110 transition-all duration-300 ease-out"
              onClick={() => navigate('/auth')}
            >
              {t.landingDemo.ctaButton}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            
            {/* Repeat Demo Button */}
            <div>
              <Button
                variant="ghost"
                size="sm"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                onClick={restartDemo}
              >
                {language === 'ca' ? 'Repetir la demo' : language === 'es' ? 'Repetir la demo' : 'Replay demo'}
              </Button>
            </div>
          </div>
          )}
          
          {/* Trust indicators below demo */}
          <div className="mt-8 space-y-8 text-center">
            <p className="text-sm text-muted-foreground/70">
              {t.trustIndicators.demoSubtext}
            </p>
            
            <div className="flex items-center justify-center gap-3 text-base text-muted-foreground/85 py-2">
              <span className="text-2xl">🔍</span>
              <span className="font-medium">{t.trustIndicators.usageCounter.replace('🔍 ', '')}</span>
            </div>

            {/* For who section */}
            <div className="pt-4">
              <p className="text-sm text-primary/70 font-semibold uppercase tracking-wider mb-3">
                {t.trustIndicators.designedForTitle}
              </p>
              <h3 className="text-base font-medium text-foreground/90 mb-4">
                {t.trustIndicators.forWhoTitle}
              </h3>
              <div className="flex flex-wrap items-center justify-center gap-2">
                <span className="px-4 py-2 text-sm rounded-full bg-primary/10 text-primary border border-primary/20">
                  {t.trustIndicators.audience1}
                </span>
                <span className="px-4 py-2 text-sm rounded-full bg-primary/10 text-primary border border-primary/20">
                  {t.trustIndicators.audience2}
                </span>
                <span className="px-4 py-2 text-sm rounded-full bg-primary/10 text-primary border border-primary/20">
                  {t.trustIndicators.audience3}
                </span>
                <span className="px-4 py-2 text-sm rounded-full bg-primary/10 text-primary border border-primary/20">
                  {t.trustIndicators.audience4}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto">
          <p className="text-center text-muted-foreground text-lg font-medium">
            {t.landingTrust.text}
          </p>
        </div>
      </section>

      {/* Time Savings Calculator */}
      <section className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto animate-scale-in">
          <Card className="p-8 md:p-12 shadow-[var(--shadow-card)] hover:shadow-xl transition-shadow bg-gradient-to-br from-card to-accent/10 border-2 border-primary/10">
            <div className="text-center mb-8">
              <Clock className="h-12 w-12 text-primary mx-auto mb-4" />
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
                {t.landingCalculator.title}
              </h2>
              <p className="text-muted-foreground text-lg">
                {t.landingCalculator.subtitle}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {t.landingCalculator.searchesLabel}
                </label>
                <Input
                  type="number"
                  value={weeklySearches}
                  onChange={(e) => setWeeklySearches(e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value) || 0))}
                  className="text-lg h-12 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  placeholder={t.landingCalculator.searchesPlaceholder}
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {t.landingCalculator.minutesLabel}
                </label>
                <Input
                  type="number"
                  value={minutesPerSearch}
                  onChange={(e) => setMinutesPerSearch(e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value) || 0))}
                  className="text-lg h-12 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  placeholder={t.landingCalculator.minutesPlaceholder}
                  min="0"
                />
              </div>
            </div>

            <div className="bg-primary/10 rounded-lg p-6 border-2 border-primary/20 relative overflow-hidden min-h-[180px] flex items-center justify-center">
              {/* Progress bar visualization */}
              {hasEnoughData && (
                <div className="absolute top-0 left-0 h-1 bg-gradient-to-r from-primary to-accent-foreground transition-all duration-1000" 
                     style={{ width: `${Math.min(100, (timeSaved / 200) * 100)}%` }} />
              )}
              
              {!hasEnoughData ? (
                <div className="text-center py-8">
                  {/* No mostrar cap missatge si els camps estan buits */}
                </div>
              ) : (
                <div className="grid md:grid-cols-3 gap-6 text-center w-full">
                  <div className="animate-fade-in" style={{ animationDelay: '0.1s' }}>
                    <p className="text-4xl font-bold text-primary mb-2">
                      {timeSaved}
                    </p>
                    <p className="text-sm text-muted-foreground">{t.landingCalculator.minutesSaved}</p>
                  </div>
                  <div className="animate-fade-in" style={{ animationDelay: '0.2s' }}>
                    <p className="text-4xl font-bold text-primary mb-2">
                      {hoursPerWeek}h {minutesRemaining}m
                    </p>
                    <p className="text-sm text-muted-foreground">{t.landingCalculator.freeTime}</p>
                  </div>
                  <div className="animate-fade-in" style={{ animationDelay: '0.3s' }}>
                    <p className="text-4xl font-bold text-primary mb-2">
                      {hoursPerMonth}h
                    </p>
                    <p className="text-sm text-muted-foreground">{t.landingCalculator.hoursSaved}</p>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16 animate-fade-in">
            <p className="text-sm text-primary font-semibold uppercase tracking-wider mb-3">
              {language === 'ca' ? 'Més que un cercador de cites' : language === 'es' ? 'Más que un buscador de citas' : 'More than a citation search engine'}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t.landingBenefits.title}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t.landingBenefits.subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <Card 
                key={index} 
                className="p-6 hover:shadow-[var(--shadow-card)] transition-all duration-300 hover:-translate-y-2 hover:border-primary/30 bg-card border-border animate-scale-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <benefit.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2">
                  {benefit.title}
                </h3>
                <p className="text-muted-foreground">
                  {benefit.description}
                </p>
              </Card>
            ))}
          </div>

          {/* Testimonial */}
          <div className="mt-16 max-w-3xl mx-auto animate-fade-in">
            <Card className="p-8 md:p-10 bg-[var(--gradient-testimonial)] shadow-[var(--shadow-testimonial)] border-primary/20 backdrop-blur-sm">
              <div className="flex flex-col items-center text-center">
                <div className="text-5xl mb-5">💬</div>
                <blockquote className="text-xl md:text-2xl italic text-foreground mb-5 leading-relaxed">
                  {t.landingBenefits.testimonialQuote}
                </blockquote>
                <cite className="text-sm text-muted-foreground not-italic font-medium">
                  {t.landingBenefits.testimonialAuthor}
                </cite>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="container mx-auto px-4 py-16 bg-gradient-to-br from-primary/5 to-accent/5 rounded-3xl my-16">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-4 animate-fade-in">
            <p className="text-sm text-primary font-semibold uppercase tracking-wider mb-2">
              {t.landingHowItWorks.subtitle}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t.landingHowItWorks.title}
            </h2>
            <p className="text-lg text-muted-foreground">
              {t.landingHowItWorks.description}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mt-12">
            {[
              {
                step: '1',
                icon: Stethoscope,
                title: t.landingHowItWorks.step1Title,
                description: t.landingHowItWorks.step1Desc,
              },
              {
                step: '2',
                icon: Search,
                title: t.landingHowItWorks.step2Title,
                description: t.landingHowItWorks.step2Desc,
              },
              {
                step: '3',
                icon: FileCheck,
                title: t.landingHowItWorks.step3Title,
                description: t.landingHowItWorks.step3Desc,
              },
            ].map((item, index) => (
              <div key={index} className="relative animate-fade-in" style={{ animationDelay: `${index * 0.2}s` }}>
                <div className="flex flex-col items-center text-center">
                  <div className="h-16 w-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-2xl font-bold mb-4 shadow-[var(--shadow-card)] relative">
                    {item.step}
                    <div className="absolute -top-2 -right-2 bg-accent rounded-full p-2">
                      <item.icon className="h-4 w-4 text-primary" />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    {item.title}
                  </h3>
                  <p className="text-muted-foreground">
                    {item.description}
                  </p>
                </div>
                {index < 2 && (
                  <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-primary/20" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="container mx-auto px-4 py-20">
        <div className="max-w-3xl mx-auto text-center">
          <div className="bg-gradient-to-br from-primary to-primary-hover rounded-2xl p-12 shadow-2xl">
            <h2 className="text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
              {t.landingCta.title}
            </h2>
            <p className="text-lg text-primary-foreground/90 mb-8">
              {t.landingCta.subtitle}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                size="lg" 
                variant="secondary"
                className="text-lg px-10 h-14 bg-background text-foreground hover:bg-background/90 hover:scale-[1.05] shadow-lg transition-all duration-300 ease-out"
                onClick={() => navigate('/auth')}
              >
                {t.landingCta.button}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Final trust text before footer */}
      <section className="py-12 text-center">
        <p className="text-sm text-muted-foreground/70 max-w-2xl mx-auto px-4">
          {t.trustIndicators.finalText}
        </p>
      </section>

      {/* Footer */}
      <footer className="border-t bg-muted/30">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto text-center space-y-4">
            <p className="text-sm text-foreground font-medium">
              📘 {t.landingFooter.copyright}
            </p>
            <p className="text-sm">
              ✉️ <a 
                href="mailto:centenera@actuaai.com" 
                className="text-primary hover:underline transition-colors"
              >
                {t.landingFooter.contact}
              </a>
            </p>
            <p className="text-sm text-muted-foreground">
              🧠 {t.landingFooter.developedBy}
            </p>
          </div>
        </div>
      </footer>
      </div>
    </>
  );
};

export default Landing;
